//#region src/js/components/search.js
/**
* Custom element that implements predictive search functionality.
* Displays search suggestions as the user types in the search input field.
* 
* @class PredictiveSearch
* @extends {HTMLElement}
* 
* @property {HTMLInputElement} input - The search input element
* @property {HTMLElement} predictiveSearchResults - The container element for displaying search results
* 
* @example
* // Usage in HTML
* <predictive-search>
*   <input type="search" />
*   <div id="predictive-search"></div>
* </predictive-search>
*/
/**
* Fetches search results from the Shopify predictive search API.
* Updates the results container with the fetched markup on success.
* 
* @method getSearchResults
* @param {string} searchTerm - The search query entered by the user
* @returns {void}
* @throws {Error} Throws an error if the fetch request fails
*/
/**
* Creates a debounced version of a function that delays its execution.
* 
* @method debounce
* @param {Function} fn - The function to debounce
* @param {number} wait - The delay in milliseconds
* @returns {Function} The debounced function
*/
var PredictiveSearch = class extends HTMLElement {
	constructor() {
		super();
		this.input = this.querySelector("input[type=\"search\"]");
		this.predictiveSearchResults = this.querySelector("#predictive-search");
		this.input.addEventListener("input", this.debounce((event) => {
			this.onChange(event);
		}, 300).bind(this));
	}
	onChange() {
		const searchTerm = this.input.value.trim();
		if (!searchTerm.length) {
			this.close();
			return;
		}
		this.getSearchResults(searchTerm);
	}
	getSearchResults(searchTerm) {
		const encodedSearchTerm = encodeURIComponent(searchTerm);
		const searchUrl = `${window.Shopify.routes.root}search/suggest?q=${encodedSearchTerm}&section_id=section-predictive-search`;
		fetch(searchUrl, { headers: { "Content-Type": "application/json" } }).then((response) => {
			if (!response.ok) {
				const error = new Error(response.status);
				this.close();
				throw error;
			}
			return response.text();
		}).then((text) => {
			const resultsMarkup = new DOMParser().parseFromString(text, "text/html").querySelector("#shopify-section-section-predictive-search").innerHTML;
			this.predictiveSearchResults.innerHTML = resultsMarkup;
			this.open();
		}).catch((error) => {
			this.close();
			throw error;
		});
	}
	open() {
		this.predictiveSearchResults.style.display = "block";
	}
	close() {
		this.predictiveSearchResults.style.display = "none";
	}
	debounce(fn, wait) {
		let t;
		return (...args) => {
			clearTimeout(t);
			t = setTimeout(() => fn.apply(this, args), wait);
		};
	}
};
customElements.define("predictive-search", PredictiveSearch);
//#endregion
